'use client'

import { useState } from 'react'
import { Share2, Check } from 'lucide-react'
import { trackEvent, trackOwnerCampaignConversion } from '@/lib/analytics'

interface Props {
  /** 'closing' = white button on blue bg; 'sticky' = full-width blue bar button. */
  variant?: 'closing' | 'sticky'
}

const SHARE_TEXT =
  'You can choose how your pet is remembered — just ask your vet for PetAftercare.'

/**
 * The /choose call to action: spread the word, no data capture. Uses the native
 * share sheet where available (mobile), falls back to copying the link.
 */
export default function ChooseShareButton({ variant = 'closing' }: Props) {
  const [copied, setCopied] = useState(false)

  async function handleShare() {
    const url = typeof window !== 'undefined' ? window.location.href : ''
    trackEvent('choose_share_clicked', { variant })
    trackOwnerCampaignConversion({ variant, action: 'share' })

    try {
      if (navigator.share) {
        await navigator.share({ title: 'PetAftercare', text: SHARE_TEXT, url })
        return
      }
    } catch {
      // user dismissed the share sheet — nothing to do
      return
    }

    // No native share (most desktops): copy the link instead.
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      /* clipboard blocked — silently ignore */
    }
  }

  if (variant === 'sticky') {
    return (
      <button
        onClick={handleShare}
        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#004da9] hover:bg-[#003d8a] text-white text-[16px] font-semibold px-5 py-3.5 rounded-full transition-colors"
      >
        {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
        {copied ? 'Link copied' : 'Share PetAftercare'}
      </button>
    )
  }

  return (
    <button
      onClick={handleShare}
      className="group inline-flex items-center gap-2 bg-white text-[#004da9] px-8 sm:px-6 py-4 sm:py-3.5 text-[18px] sm:text-[16px] rounded-full font-semibold hover:bg-[#f0f6fb] transition-colors shadow-2xl shadow-black/20"
    >
      {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
      {copied ? 'Link copied — thank you' : 'Share with a pet owner'}
    </button>
  )
}
